/**
 * Trunnion AI - Tool Call Card
 * Displays details of a tool invocation.
 */

import { Wrench, CheckCircle, XCircle, Clock, ShieldOff } from 'lucide-react';
import { Badge } from '@/components/ui/Badge';
import { CodeBlock } from '@/components/ui/CodeBlock';
import type { ToolCallRecord } from '@/types';

interface ToolCallCardProps {
  toolCall: ToolCallRecord;
}

const statusConfig: Record<string, { icon: React.ComponentType<{ className?: string }>; variant: 'success' | 'error' | 'info' | 'warning' | 'default' }> = {
  success: { icon: CheckCircle, variant: 'success' },
  failed: { icon: XCircle, variant: 'error' },
  running: { icon: Clock, variant: 'info' },
  pending: { icon: Clock, variant: 'default' },
  denied: { icon: ShieldOff, variant: 'warning' },
};

export function ToolCallCard({ toolCall }: ToolCallCardProps) {
  const config = statusConfig[toolCall.status] ?? statusConfig.pending;
  const StatusIcon = config.icon;

  return (
    <div className="rounded-lg border border-trunnion-slate-700 bg-trunnion-slate-800/50">
      {/* Header */}
      <div className="flex items-center justify-between border-b border-trunnion-slate-700/50 px-3 py-2">
        <div className="flex items-center gap-2">
          <Wrench className="h-4 w-4 text-trunnion-gold-500" />
          <span className="text-sm font-medium text-white">
            {toolCall.toolName}
          </span>
        </div>
        <Badge variant={config.variant} dot>
          <StatusIcon className="h-3 w-3 mr-1" />
          {toolCall.status}
        </Badge>
      </div>

      {/* Parameters */}
      {Object.keys(toolCall.parameters).length > 0 && (
        <div className="px-3 py-2 border-b border-trunnion-slate-700/50">
          <p className="text-xs font-medium text-trunnion-slate-400 mb-1">
            Parameters
          </p>
          <CodeBlock
            content={JSON.stringify(toolCall.parameters, null, 2)}
            language="json"
            maxHeight="120px"
          />
        </div>
      )}

      {/* Result */}
      {toolCall.result && (
        <div className="px-3 py-2">
          <p className="text-xs font-medium text-trunnion-slate-400 mb-1">
            Result
          </p>
          <CodeBlock
            content={JSON.stringify(toolCall.result, null, 2)}
            language="json"
            maxHeight="120px"
          />
        </div>
      )}

      {/* Error */}
      {toolCall.error && (
        <div className="px-3 py-2">
          <p className="text-xs text-red-400">{toolCall.error}</p>
        </div>
      )}

      {/* Policy evaluation */}
      {toolCall.policyEvaluation && (
        <div className="border-t border-trunnion-slate-700/50 px-3 py-2">
          <p className="text-xs text-trunnion-slate-400">
            Policy: {toolCall.policyEvaluation.allowed ? 'Allowed' : 'Denied'}{' '}
            <span className="text-trunnion-slate-500">
              ({toolCall.policyEvaluation.reason})
            </span>
          </p>
        </div>
      )}
    </div>
  );
}
